/**
 * LRU (Least Recently Used) Cache implementation
 * Provides efficient caching with automatic eviction of old entries
 */

/**
 * Cache entry with metadata
 */
interface CacheEntry<T> {
  value: T;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
  size?: number;
}

/**
 * Cache statistics
 */
export interface CacheStatistics {
  entries: number;
  hits: number;
  misses: number;
  hitRate: number;
  evictions: number;
  totalSize: number;
  oldestEntry: number | null;
  newestEntry: number | null;
}

/**
 * LRU Cache implementation with TTL support
 */
export class LRUCache<K, V> {
  private cache: Map<K, CacheEntry<V>>;
  private accessOrder: K[];
  private maxEntries: number;
  private ttl: number; // Time to live in milliseconds
  private stats: {
    hits: number;
    misses: number;
    evictions: number;
  };

  /**
   * Create a new LRU cache
   * @param maxEntries - Maximum number of entries
   * @param ttl - Time to live in milliseconds (0 = no expiry)
   */
  constructor(maxEntries: number = 100, ttl: number = 0) {
    this.cache = new Map();
    this.accessOrder = [];
    this.maxEntries = maxEntries;
    this.ttl = ttl;
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0
    };
  }

  /**
   * Get a value from the cache
   * @param key - Cache key
   * @returns Cached value or undefined
   */
  get(key: K): V | undefined {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.misses++;
      return undefined;
    }

    // Check if entry has expired
    if (this.isExpired(entry)) {
      this.delete(key);
      this.stats.misses++;
      return undefined;
    }

    // Update access order
    this.updateAccessOrder(key);
    
    // Update entry metadata
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    
    this.stats.hits++;
    return entry.value;
  }

  /**
   * Set a value in the cache
   * @param key - Cache key
   * @param value - Value to cache
   * @param size - Optional size of the value
   */
  set(key: K, value: V, size?: number): void {
    // Remove existing entry if present
    if (this.cache.has(key)) {
      this.delete(key);
    }

    // Evict oldest entry if at capacity
    if (this.cache.size >= this.maxEntries) {
      this.evictOldest();
    }

    // Create new entry
    const now = Date.now();
    const entry: CacheEntry<V> = {
      value,
      timestamp: now,
      accessCount: 0,
      lastAccessed: now,
      size
    };

    this.cache.set(key, entry);
    this.accessOrder.push(key);
  }

  /**
   * Check if cache has a key
   * @param key - Cache key
   * @returns True if key exists and is not expired
   */
  has(key: K): boolean {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }

    if (this.isExpired(entry)) {
      this.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Delete an entry from the cache
   * @param key - Cache key
   * @returns True if entry was deleted
   */
  delete(key: K): boolean {
    const hadEntry = this.cache.delete(key);
    
    if (hadEntry) {
      const index = this.accessOrder.indexOf(key);
      if (index > -1) {
        this.accessOrder.splice(index, 1);
      }
    }

    return hadEntry;
  }

  /**
   * Clear all entries from the cache
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0
    };
  }

  /**
   * Get the size of the cache
   * @returns Number of entries
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  getStats(): CacheStatistics {
    let totalSize = 0;
    let oldestEntry: number | null = null;
    let newestEntry: number | null = null;

    this.cache.forEach(entry => {
      if (entry.size) {
        totalSize += entry.size;
      }
      
      if (oldestEntry === null || entry.timestamp < oldestEntry) {
        oldestEntry = entry.timestamp;
      }
      
      if (newestEntry === null || entry.timestamp > newestEntry) {
        newestEntry = entry.timestamp;
      }
    });

    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? (this.stats.hits / total) * 100 : 0;

    return {
      entries: this.cache.size,
      hits: this.stats.hits,
      misses: this.stats.misses,
      hitRate: Math.round(hitRate * 100) / 100,
      evictions: this.stats.evictions,
      totalSize,
      oldestEntry,
      newestEntry
    };
  }

  /**
   * Get all keys in the cache
   * @returns Array of cache keys
   */
  keys(): K[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get all values in the cache
   * @returns Array of cache values
   */
  values(): V[] {
    const values: V[] = [];
    
    this.cache.forEach(entry => {
      if (!this.isExpired(entry)) {
        values.push(entry.value);
      }
    });
    
    return values;
  }

  /**
   * Get all entries in the cache
   * @returns Array of [key, value] pairs
   */
  entries(): Array<[K, V]> {
    const entries: Array<[K, V]> = [];
    
    this.cache.forEach((entry, key) => {
      if (!this.isExpired(entry)) {
        entries.push([key, entry.value]);
      }
    });
    
    return entries;
  }

  /**
   * Prune expired entries
   * @returns Number of entries removed
   */
  prune(): number {
    if (this.ttl === 0) {
      return 0;
    }

    let removed = 0;
    const keysToDelete: K[] = [];

    this.cache.forEach((entry, key) => {
      if (this.isExpired(entry)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      this.delete(key);
      removed++;
    });

    return removed;
  }

  /**
   * Check if an entry has expired
   * @param entry - Cache entry
   * @returns True if expired
   */
  private isExpired(entry: CacheEntry<V>): boolean {
    if (this.ttl === 0) {
      return false;
    }

    const age = Date.now() - entry.timestamp;
    return age > this.ttl;
  }

  /**
   * Update the access order for a key
   * @param key - Cache key
   */
  private updateAccessOrder(key: K): void {
    const index = this.accessOrder.indexOf(key);
    
    if (index > -1) {
      // Remove from current position
      this.accessOrder.splice(index, 1);
    }
    
    // Add to end (most recently used)
    this.accessOrder.push(key);
  }

  /**
   * Evict the oldest (least recently used) entry
   */
  private evictOldest(): void {
    if (this.accessOrder.length === 0) {
      return;
    }

    const oldestKey = this.accessOrder[0];
    this.delete(oldestKey);
    this.stats.evictions++;
  }

  /**
   * Get memory size estimate for a value
   * @param value - Value to estimate
   * @returns Estimated size in bytes
   */
  static estimateSize(value: unknown): number {
    if (value === null || value === undefined) {
      return 0;
    }

    if (typeof value === 'string') {
      return value.length * 2; // Rough estimate for UTF-16
    }

    if (typeof value === 'number') {
      return 8;
    }

    if (typeof value === 'boolean') {
      return 4;
    }

    if (value instanceof Date) {
      return 24;
    }

    if (Array.isArray(value)) {
      return value.reduce((sum, item) => sum + LRUCache.estimateSize(item), 24);
    }

    if (typeof value === 'object') {
      let size = 24; // Object overhead
      
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          size += LRUCache.estimateSize(key);
          size += LRUCache.estimateSize((value as any)[key]);
        }
      }
      
      return size;
    }

    return 0;
  }
}

/**
 * Retry mechanism with exponential backoff
 */
export class RetryHandler {
  private maxRetries: number;
  private baseDelay: number;
  private maxDelay: number;
  private factor: number;

  /**
   * Create a new retry handler
   * @param maxRetries - Maximum number of retries
   * @param baseDelay - Base delay in milliseconds
   * @param maxDelay - Maximum delay in milliseconds
   * @param factor - Exponential factor
   */
  constructor(
    maxRetries: number = 3,
    baseDelay: number = 1000,
    maxDelay: number = 30000,
    factor: number = 2
  ) {
    this.maxRetries = maxRetries;
    this.baseDelay = baseDelay;
    this.maxDelay = maxDelay;
    this.factor = factor;
  }

  /**
   * Execute a function with retry logic
   * @param fn - Function to execute
   * @param shouldRetry - Function to determine if retry should happen
   * @returns Result of the function
   */
  async execute<T>(
    fn: () => Promise<T>,
    shouldRetry?: (error: any, attempt: number) => boolean
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;

        // Check if we should retry
        if (attempt === this.maxRetries) {
          break;
        }

        if (shouldRetry && !shouldRetry(error, attempt)) {
          break;
        }

        // Calculate delay
        const delay = this.calculateDelay(attempt);
        await this.sleep(delay);
      }
    }

    throw lastError;
  }

  /**
   * Calculate delay for a given attempt
   * @param attempt - Attempt number (0-based)
   * @returns Delay in milliseconds
   */
  private calculateDelay(attempt: number): number {
    const delay = this.baseDelay * Math.pow(this.factor, attempt);
    return Math.min(delay, this.maxDelay);
  }

  /**
   * Sleep for a given duration
   * @param ms - Milliseconds to sleep
   * @returns Promise that resolves after delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}